import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PhoneCall, X } from "lucide-react";
import api from "@/lib/api";
import { uploadSegment, finalizeMeeting, audioExt } from "@/lib/meetingUpload";
import ConsentNotice from "@/components/ConsentNotice";
import { Button } from "@/components/ui/button";

const SOON = true;

export default function CallImportButton({ leadId, className = "", label = "Importer un appel" }) {
  if (SOON) return (
    <button type="button" disabled aria-disabled="true" data-testid="call-import-btn" title="Bientôt disponible"
      className={`${className} !bg-secondary/50 !text-muted-foreground opacity-60 cursor-not-allowed flex-col !gap-0 leading-tight`}>
      <span className="inline-flex items-center gap-1.5"><PhoneCall className="w-4 h-4" /> {label}</span>
      <span className="text-[9px] font-normal" data-testid="call-import-soon">(bientôt disponible)</span>
    </button>
  );
  return <CallImportActive leadId={leadId} className={className} label={label} />;
}

function CallImportActive({ leadId, className, label }) {
  const navigate = useNavigate();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState(null);

  const send = async () => {
    setProgress(0);
    try {
      const { data } = await api.post("/meetings", { kind: "call", leadId: leadId || null, title: "" });
      await uploadSegment(data.id, 0, file, audioExt(file.type, file.name), setProgress);
      await finalizeMeeting(data.id, 0);
      navigate(`/reunions/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import impossible, réessayez");
      setProgress(null);
    }
  };

  return (
    <>
      <button type="button" onClick={() => input.current?.click()} data-testid="call-import-btn" className={className}>
        <PhoneCall className="w-4 h-4" /> {label}
      </button>
      <input ref={input} type="file" accept="audio/*,.m4a,.mp3,.amr,.3gp,.aac,.wav,.ogg" hidden data-testid="call-import-input"
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) { window.dispatchEvent(new CustomEvent("birdie:close")); setFile(f); setConsent(false); } }} />
      {file && createPortal(
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4" data-testid="call-import-dialog">
          <div className="w-full max-w-md klt-glow-card rounded-3xl p-6 space-y-4 relative">
            {progress === null && <button onClick={() => setFile(null)} className="absolute top-4 right-4 text-slate-400" data-testid="call-import-close"><X className="w-5 h-5" /></button>}
            <h2 className="font-heading text-xl font-extrabold flex items-center gap-2"><PhoneCall className="w-5 h-5 text-[#60A5FA]" /> Synthèse d'appel</h2>
            <p className="text-sm text-muted-foreground truncate" data-testid="call-import-filename">{file.name} · {(file.size / 1048576).toFixed(1)} Mo</p>
            <ConsentNotice checked={consent} onChange={setConsent} testid="call-consent" />
            {progress !== null && (
              <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-[#3B82F6] transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
            )}
            <Button onClick={send} disabled={!consent || progress !== null} data-testid="call-import-send" className="w-full h-12 rounded-2xl klt-fab-neon font-semibold">
              {progress === null ? "Analyser l'appel" : `Envoi ${Math.round(progress * 100)} %`}
            </Button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
